import { useEffect, useRef, useState } from 'react'
import StoryMediaError from './StoryMediaError'
import StoryVideoControls from './StoryVideoControls'
import type { StoryVideoMedia } from './storyMedia'

export interface StoryVideoElementProps {
	media: StoryVideoMedia
	onTogglePlayback?: () => void
	onToggleMuted?: () => void
	onPlaybackBlocked?: () => void
	onPlaybackTimeChange?: (currentTime: number) => void
	onRetry?: () => Promise<void>
	onLoadingChange?: (loading: boolean) => void
}

type VideoElementStatus = 'loading' | 'ready' | 'error'

export default function StoryVideoElement({
	media,
	onTogglePlayback,
	onToggleMuted,
	onPlaybackBlocked,
	onPlaybackTimeChange,
	onRetry,
	onLoadingChange,
}: StoryVideoElementProps) {
	const videoRef = useRef<HTMLVideoElement>(null)
	const [elementStatus, setElementStatus] = useState<VideoElementStatus>('loading')
	const [reloadKey, setReloadKey] = useState(0)
	const elementErrorMessage = elementStatus === 'error' ? '视频暂时无法播放' : undefined
	const errorMessage = media.errorMessage ?? elementErrorMessage
	const isLoading = !errorMessage && (media.loading || elementStatus === 'loading')

	const handleReady = () => {
		setElementStatus('ready')
		onLoadingChange?.(false)
	}

	const handleWaiting = () => {
		setElementStatus('loading')
		onLoadingChange?.(true)
	}

	const handleError = () => {
		setElementStatus('error')
		onLoadingChange?.(false)
	}

	const handleRetry = () => {
		setElementStatus('loading')
		onLoadingChange?.(true)

		const reloadVideo = () => setReloadKey((key) => key + 1)
		if (onRetry) {
			void onRetry().then(reloadVideo, reloadVideo)
		} else {
			reloadVideo()
		}
	}

	const handleLoadedMetadata = () => {
		const video = videoRef.current
		if (!video || !media.initialTime || !Number.isFinite(video.duration)) return

		video.currentTime = Math.min(media.initialTime, Math.max(0, video.duration - 0.1))
	}

	const handleTimeUpdate = () => {
		const currentTime = videoRef.current?.currentTime
		if (currentTime !== undefined) onPlaybackTimeChange?.(currentTime)
	}

	useEffect(() => {
		const video = videoRef.current
		if (!video || !media.url) return

		if (!media.isPlaying) {
			video.pause()
			return
		}

		void video.play().catch(() => onPlaybackBlocked?.())
	}, [media.isPlaying, media.url, onPlaybackBlocked, reloadKey])

	return (
		<>
			<video
				key={reloadKey}
				ref={videoRef}
				src={media.url}
				poster={media.posterUrl}
				aria-label={media.alt}
				muted={media.isMuted}
				autoPlay={media.isPlaying}
				loop
				playsInline
				preload="metadata"
				onLoadedMetadata={handleLoadedMetadata}
				onTimeUpdate={handleTimeUpdate}
				onCanPlay={handleReady}
				onPlaying={handleReady}
				onWaiting={handleWaiting}
				onError={handleError}
			/>
			{errorMessage && <StoryMediaError message={errorMessage} onRetry={handleRetry} />}
			{!isLoading && !errorMessage && (
				<StoryVideoControls
					isPlaying={media.isPlaying}
					isMuted={media.isMuted}
					onTogglePlayback={onTogglePlayback}
					onToggleMuted={onToggleMuted}
				/>
			)}
		</>
	)
}
